import { Box } from '@mui/material';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import PayappTable from '../file-upload/PayappTable';
import { usePayappProcess } from '../../context/PayappProcessContext';
import { findDivisionsByDescriptions } from '../file-upload/payapp.api';
import { toast } from 'react-toastify';

const TableVerification: React.FC = () => {
  const {
    state,
    setFieldHeadersMapping,
    updateTableRows,
    setSelectedRowIds,
    setLineItemDivisions,
    detectOCRIssues,
    fixOCRIssues,
    getOCRIssuesCount,
  } = usePayappProcess();
  const [assignedTotals, setAssignedTotals] = useState<Record<string, string>>({});
  const [fetchingDivisions, setFetchingDivisions] = useState<boolean>(false);

  // Ref to track the current description field we've fetched divisions for
  const fetchedForDescriptionField = useRef<string | null>(null);

  // Ref to track if we've already applied divisions to the current rows
  const hasAppliedDivisions = useRef<boolean>(false);

  // Ref to avoid running OCR detection more than once per table load (guards against
  // repeated runs if tableRows reference or ocrIssuesDetected flips)
  const ocrDetectionRunForRows = useRef<number>(0);

  // Detect OCR issues when table rows are loaded (once per table content)
  useEffect(() => {
    const rowCount = state.tableRows?.length ?? 0;
    if (rowCount === 0) {
      ocrDetectionRunForRows.current = 0;
      return;
    }
    if (state.ocrIssuesDetected) return;
    // Only run once per "load" (same row count); skip if we already ran for this count
    if (ocrDetectionRunForRows.current === rowCount) return;
    ocrDetectionRunForRows.current = rowCount;
    detectOCRIssues();
  }, [state.tableRows?.length, state.ocrIssuesDetected, detectOCRIssues]);

  // Wrapper functions to match the expected types for PayappTable props
  const handleSetFieldHeadersMapping = useCallback(
    (value: React.SetStateAction<Record<string, string>>) => {
      if (typeof value === 'function') {
        setFieldHeadersMapping(value(state.fieldHeadersMapping));
      } else {
        setFieldHeadersMapping(value);
      }
    },
    [state.fieldHeadersMapping, setFieldHeadersMapping],
  );

  const handleSetRows = useCallback(
    (value: React.SetStateAction<any[]>) => {
      if (typeof value === 'function') {
        updateTableRows(value(state.tableRows));
      } else {
        updateTableRows(value);
      }
    },
    [state.tableRows, updateTableRows],
  );

  // Handle OCR fix button click
  const handleFixOCRIssues = useCallback(() => {
    const issuesCount = getOCRIssuesCount();
    if (issuesCount === 0) {
      toast.info('No currency format issues detected');
      return;
    }

    fixOCRIssues();
    toast.success(`Fixed ${issuesCount} currency format issues`);
  }, [fixOCRIssues, getOCRIssuesCount]);

  // Check if description column is selected and fetch divisions
  useEffect(() => {
    const descriptionField = Object.entries(state.fieldHeadersMapping).find(([_, headerName]) => headerName === 'Description');

    // If no description field, reset our tracking refs
    if (!descriptionField) {
      hasAppliedDivisions.current = false;
      return;
    }

    // If the description field changed, we need to re-fetch
    const descriptionFieldChanged =
      fetchedForDescriptionField.current !== null && fetchedForDescriptionField.current !== descriptionField[0];

    if (descriptionFieldChanged) {
      // Description field assignment changed, reset tracking
      hasAppliedDivisions.current = false;
    }

    // Fetch divisions if:
    // 1. We have a description field mapping
    // 2. We're not currently fetching
    // 3. We haven't applied divisions yet for this description field OR the description field changed
    // 4. We have rows to process
    if (descriptionField && !fetchingDivisions && !hasAppliedDivisions.current && state.tableRows?.length > 0) {
      fetchDivisions(descriptionField[0]);
    } else if (descriptionField && state.lineItemDivisions?.length > 0 && !hasAppliedDivisions.current) {
      // If we already have division data in context but haven't applied it to the rows yet
      applyExistingDivisions(descriptionField[0]);
    }
  }, [state.fieldHeadersMapping, fetchingDivisions, state.tableRows, state.lineItemDivisions]);

  // Apply existing divisions from context to the current rows
  const applyExistingDivisions = (descriptionField: string) => {
    // Only apply if we have both table rows and divisions
    if (!state.tableRows?.length || !state.lineItemDivisions?.length) return;

    // Update rows with existing division data
    const updatedRows = state.tableRows.map((row, index) => {
      // Only update if we have division data for this index
      if (index < state.lineItemDivisions.length) {
        const matchingItem = state.lineItemDivisions[index];
        // Only override if row doesn't already have a division
        if (!row.division && matchingItem?.division) {
          return {
            ...row,
            division: matchingItem.division,
          };
        }
      }
      return row;
    });

    // Mark as applied
    fetchedForDescriptionField.current = descriptionField;
    hasAppliedDivisions.current = true;

    // Update rows if anything changed
    if (JSON.stringify(updatedRows) !== JSON.stringify(state.tableRows)) {
      updateTableRows(updatedRows);
    }
  };

  const fetchDivisions = async (descriptionField: string) => {
    if (!state.tableRows?.length) return;

    setFetchingDivisions(true);
    try {
      // Extract descriptions from rows
      const descriptions = state.tableRows.map(row => row[descriptionField]);

      // Call API to get divisions
      const lineItemDivisionsResponse = await findDivisionsByDescriptions(descriptions);

      // Update state with divisions
      setLineItemDivisions(lineItemDivisionsResponse);

      // Add division to each row
      const updatedRows = state.tableRows.map((row, index) => {
        const matchingItem = lineItemDivisionsResponse[index];
        // Only override if row doesn't already have a division
        if (!row.division && matchingItem?.division) {
          return {
            ...row,
            division: matchingItem?.division || null,
          };
        }
        return row;
      });

      // Store the field we've fetched divisions for
      fetchedForDescriptionField.current = descriptionField;
      hasAppliedDivisions.current = true;

      updateTableRows(updatedRows);
    } catch (error) {
      console.error('Error fetching divisions:', error);
      toast.error('Failed to fetch divisions for line items');
    } finally {
      setFetchingDivisions(false);
    }
  };

  return (
    <Box sx={{ minHeight: '750px' }}>
      <PayappTable
        data={state.tableRows}
        onTotalsChange={setAssignedTotals}
        onSelectionChange={setSelectedRowIds}
        fieldHeadersMapping={state.fieldHeadersMapping}
        setFieldHeadersMapping={handleSetFieldHeadersMapping}
        rows={state.tableRows}
        setRows={handleSetRows}
        onFixOCRIssues={handleFixOCRIssues}
      />
    </Box>
  );
};

export default TableVerification;
